import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Download, FileText, Loader2, LogOut, AlertCircle, CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronUp, MapPin, Copy, Check } from 'lucide-react';

// Smart backend URL detection that works in preview and deployment
const getBackendURL = () => {
  // First try environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback for different environments
  const origin = window.location.origin;

  // If we're in localhost (development), use localhost backend
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    return 'http://localhost:8001';
  }

  // For deployed environments, use the same origin (report generator should be proxied)
  // Or use a specific subdomain/port if configured
  return origin;
};

const API_BASE_URL = getBackendURL();

function AdminRiskReport() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    recipient_email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    flood_zone_override: '',
  });
  const [loading, setLoading] = useState(false);
  const [showFloodZone, setShowFloodZone] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [addressCopied, setAddressCopied] = useState(false);

  const handleAutofillTest = () => {
    setFormData((prev) => ({
      ...prev,
      first_name: 'P',
      last_name: 'Sherman',
      recipient_email: 'PSherman@wallabyway.com',
      street_address: '42 Wallaby Way',
      city: 'Sydney',
      state: 'NY',
      zip_code: '10573',
    }));
    toast.info('Test location filled in');
  };

  const handleCopyCoords = async () => {
    const parts = [
      formData.street_address,
      formData.city,
      formData.state,
      formData.zip_code,
    ].filter(Boolean);
    if (!parts.length) {
      toast.error('No address entered to geocode');
      return;
    }
    const token = localStorage.getItem('admin_token');
    try {
      const query = encodeURIComponent(parts.join(', '));
      const res = await fetch(`${API_BASE_URL}/api/geocode?address=${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not geocode address — try adding street + ZIP');
        return;
      }
      const { latitude, longitude } = await res.json();
      const coordStr = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
      await navigator.clipboard.writeText(coordStr);
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2000);
      toast.success(`Copied: ${coordStr}`);
    } catch {
      toast.error('Failed to geocode address');
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      // No token - redirect to login silently (no error message)
      navigate('/admin/login', { replace: true });
    }
    // Don't validate token here - let the API call handle it
    // This prevents unnecessary redirects and error messages
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.info('Logged out');
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.street_address && !formData.zip_code) {
      toast.error('Please provide at least a street address or ZIP code');
      return;
    }

    if (!formData.state && !formData.zip_code) {
      toast.error('Please provide at least a state or ZIP code');
      return;
    }

    setLoading(true);
    setGeneratedReport(null);

    try {
      const token = localStorage.getItem('admin_token');

      if (!token) {
        toast.error('No authentication token found. Please login again.');
        navigate('/admin/login');
        return;
      }

      console.log('Sending request with token:', token.substring(0, 20) + '...');

      const response = await fetch(`${API_BASE_URL}/api/admin/risk-report/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        setGeneratedReport({
          filename: data.filename,
          report_id: data.report_id,
          source_summary: data.source_summary || {},
        });
        toast.success('Report generated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Report generation error:', response.status, errorData);

        if (response.status === 401) {
          // Token expired or invalid
          console.error(
            'Authentication failed. Token:',
            localStorage.getItem('admin_token')?.substring(0, 20) + '...'
          );
          localStorage.removeItem('admin_token');
          navigate('/admin/login');
          toast.error(errorData.detail || 'Session expired. Please login again.');
        } else {
          toast.error(errorData.detail || 'Failed to generate report');
        }
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!generatedReport) return;

    try {
      const token = localStorage.getItem('admin_token');

      if (!token) {
        toast.error('No authentication token found. Please login again.');
        navigate('/admin/login');
        return;
      }

      // URL encode the filename to handle special characters
      const encodedFilename = encodeURIComponent(generatedReport.filename);
      const downloadUrl = `${API_BASE_URL}/api/admin/risk-report/download/${encodedFilename}`;

      console.log('Downloading from:', downloadUrl);

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Download response status:', response.status);

      if (response.ok) {
        // Get the blob and create download link
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = generatedReport.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report downloaded successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Download error:', response.status, errorData);

        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin/login');
          toast.error('Session expired. Please login again.');
        } else if (response.status === 404) {
          toast.error('Report file not found. It may have been deleted or moved.');
        } else {
          toast.error(errorData.detail || 'Failed to download report');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Failed to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to download report: ' + error.message);
      }
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'var(--page-bg)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Risk Assessment Report Generator
            </h1>
            <p className="text-text-muted">Generate PDF risk assessment reports for customers</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>

        {/* Customer Information Form */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Customer Information</CardTitle>
                <CardDescription>
                  Enter customer details to generate a personalized risk assessment report
                </CardDescription>
              </div>
              <button
                type="button"
                onClick={handleAutofillTest}
                className="flex-shrink-0 text-xs px-2 py-1 rounded border border-text-muted/30 text-text-muted hover:text-text-primary hover:border-text-muted/60 transition-colors"
                title="Fill in test location (42 Wallaby Way)"
              >
                Autofill test
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerateReport} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="recipient_email">Email (Optional)</Label>
                <Input
                  id="recipient_email"
                  name="recipient_email"
                  type="email"
                  value={formData.recipient_email}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="street_address">Street Address</Label>
                <Input
                  id="street_address"
                  name="street_address"
                  value={formData.street_address}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleInputChange} />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={(e) => {
                      const upperValue = e.target.value.toUpperCase();
                      setFormData((prev) => ({
                        ...prev,
                        state: upperValue,
                      }));
                    }}
                    maxLength={2}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Flood Zone Override (collapsible) */}
              <div className="border border-dashed border-text-muted/30 rounded-lg p-3">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-sm text-text-muted hover:text-text-primary transition-colors"
                  onClick={() => setShowFloodZone((v) => !v)}
                >
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Flood Zone Override
                    {formData.flood_zone_override && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 text-xs font-medium">
                        {formData.flood_zone_override}
                      </span>
                    )}
                  </span>
                  {showFloodZone ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showFloodZone && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-text-muted">
                        Look up the zone at{' '}
                        <a
                          href="https://msc.fema.gov/portal/home"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-blue-400 hover:text-blue-300"
                        >
                          msc.fema.gov
                        </a>
                        , then select the result below.
                      </p>
                      <button
                        type="button"
                        onClick={handleCopyCoords}
                        className="flex-shrink-0 flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-text-muted/30 text-text-muted hover:text-text-primary hover:border-text-muted/60 transition-colors"
                        title="Geocode address and copy lat/lng to clipboard"
                      >
                        {addressCopied ? (
                          <><Check className="h-3 w-3 text-green-400" /> Copied</>
                        ) : (
                          <><Copy className="h-3 w-3" /> Copy coords</>
                        )}
                      </button>
                    </div>
                    <select
                      id="flood_zone_override"
                      name="flood_zone_override"
                      value={formData.flood_zone_override}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <option value="">— Select flood zone —</option>
                      <optgroup label="Minimal / Low Risk">
                        <option value="X (Unshaded)">X (Unshaded) — Minimal flood hazard</option>
                        <option value="X (Shaded)">X (Shaded) — Moderate flood hazard (500-yr)</option>
                        <option value="B">B — Moderate hazard (older maps)</option>
                        <option value="C">C — Minimal hazard (older maps)</option>
                      </optgroup>
                      <optgroup label="High Risk — Special Flood Hazard Area">
                        <option value="AE">AE — Base flood, detailed study</option>
                        <option value="A">A — Base flood, no detailed study</option>
                        <option value="AO">AO — Shallow flooding (sheet flow)</option>
                        <option value="AH">AH — Shallow flooding (ponding)</option>
                        <option value="AR">AR — Flood control restoration zone</option>
                        <option value="A99">A99 — Protected by flood control in progress</option>
                      </optgroup>
                      <optgroup label="Coastal High Risk">
                        <option value="VE">VE — Coastal with wave action, detailed study</option>
                        <option value="V">V — Coastal with wave action, no detailed study</option>
                      </optgroup>
                      <optgroup label="Undetermined">
                        <option value="D">D — Undetermined (not studied)</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-text-muted/70">
                      Leave blank to let the report attempt auto-detection. Only set this if the report says it could not determine the zone.
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Generated Report Section */}
        {generatedReport && (
          <Card className="border-primary/40 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <CheckCircle className="h-5 w-5" />
                Report Generated Successfully
              </CardTitle>
              <CardDescription>{generatedReport.filename}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleDownloadReport} className="w-full">
                <Download className="mr-2 h-4 w-4" />
                Download PDF Report
              </Button>

              {/* Source status summary */}
              {generatedReport.source_summary && (
                <div className="mt-4 space-y-3">
                  {/* Scored sources */}
                  {generatedReport.source_summary.scored_total > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
                        Scored Sources
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        {generatedReport.source_summary.scored_ok === generatedReport.source_summary.scored_total ? (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
                        )}
                        <span className="text-text-primary">
                          {generatedReport.source_summary.scored_ok} / {generatedReport.source_summary.scored_total} sources retrieved successfully
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Enrichment sources */}
                  {generatedReport.source_summary.enrichment && Object.keys(generatedReport.source_summary.enrichment).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
                        Enrichment Sources
                      </p>
                      <div className="grid grid-cols-1 gap-1">
                        {Object.entries(generatedReport.source_summary.enrichment).map(([label, status]) => (
                          <div key={label} className="flex items-center gap-2 text-sm">
                            {status === 'ok' ? (
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            ) : status === 'error' ? (
                              <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                            ) : (
                              <MinusCircle className="h-4 w-4 text-text-muted flex-shrink-0" />
                            )}
                            <span className={status === 'ok' ? 'text-text-primary' : 'text-text-muted'}>
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-6 border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold mb-1 text-text-primary">Note:</p>
                <p className="text-text-muted">
                  Report generation may take 30-60 seconds as it analyzes multiple data sources.
                  Please ensure you have entered a valid address (street address or ZIP code
                  required).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminRiskReport;
